// The shapes every reader of the sonar picture agrees on, and the breath of
// a broadcast ring (R230 step two, R453).
//
// Detection itself is server-authoritative — the server never sends fleets
// your faction cannot hear — so all of this is presentation. The figures
// ride presentation.toml [presentation.sonar] (R312, R323), and
// `fast/layers/sonar.ts` names them.
//
// `SonarSource` lives here too. The listening circle a site broadcasts is
// the same contract as the ring a hull carries, and the site layer owned it
// only because that layer built the list first.

import { SONAR_FLEET_RING as FLEET_RING, SONAR_SHARE_RING as SHARE_RING } from '../gen/presentation';

/**
 * The alpha of one coverage ring this frame.
 *
 * A ring that is not a broadcast is the plain flat alpha. A broadcast ring
 * breathes between `SHARE_RING.alpha` and `alpha * (1 - pulseDepth)` at
 * `pulseHz`, so a reader tells a lent array from a plain one with no legend
 * and with no second colour. The tint stays ACCENT: the pulse is the whole
 * signal (ui-map-interface.md §5).
 *
 * The ring of the selected fleet takes `FLEET_RING.selectedAlpha` in place
 * of both figures (R2210), so the reader finds the ring of the hull on the
 * card. A selected broadcast keeps its breath about that brighter alpha.
 *
 * `t` is the layer's own clock in seconds. Pure, so the vitest reads it.
 */
export function ringAlpha(sharing: boolean, t: number, selected = false): number {
  const top = selected ? FLEET_RING.selectedAlpha : SHARE_RING.alpha;
  if (!sharing) return selected ? top : FLEET_RING.alpha;
  const wave = 0.5 - 0.5 * Math.sin(2 * Math.PI * SHARE_RING.pulseHz * t);
  return top * (1 - SHARE_RING.pulseDepth * wave);
}

/**
 * One live ping sweep, as anything that reacts to the wavefront sees it
 * (R101 ping-lit snow). `strength` is the same 1→0 ramp the sweep stroke
 * fades on, so a layer keying off it dims in step with the ring the player
 * can see. Read from the ping the sonar layer is already driving — there is
 * exactly one clock for a sweep.
 */
export interface PingFront { x: number; y: number; r: number; strength: number }

/** The listening circle of the base under the cursor (§5). */
export interface HoverRing { x: number; y: number; range: number; own: boolean }

/** A structure that listens, as the sonar layer takes it (§5). */
export interface SonarSource { x: number; y: number; range: number }
